<script setup>
import { onMounted, ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ResultVisualization from './components/ResultVisualization.vue';
import DataAssistantAPI from 'dashboard/api/dataAssistant';
import { openConversationFilter } from './helpers/openConversationFilter';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const conversations = useMapGetter('dataAssistant/getConversations');
const messages = useMapGetter('dataAssistant/getMessages');
const uiFlags = useMapGetter('dataAssistant/getUIFlags');
const progressMessage = useMapGetter('dataAssistant/getProgressMessage');

const reports = useMapGetter('dataAssistantReports/getReports');

const question = ref('');
const messagesContainer = ref(null);
const questionTextarea = ref(null);
const selectedConversationId = ref(null);
const saveDropdownMsgId = ref(null);
const newReportName = ref('');
const showNewReportInput = ref(false);
const deleteDialogRef = ref(null);
const pendingDeleteId = ref(null);

// Inline editing state for message content
const editingMsgId = ref(null);
const editingMsgContent = ref('');

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

watch(
  () => messages.value.length,
  () => {
    nextTick(scrollToBottom);
  }
);

watch(
  () => uiFlags.value.isAsking,
  (newVal, oldVal) => {
    nextTick(scrollToBottom);
    if (oldVal && !newVal) {
      nextTick(() => questionTextarea.value?.focus());
    }
  }
);

const navigateToConversation = id => {
  const accountId = route.params.accountId;
  if (id) {
    router.replace({
      name: 'data_assistant_conversation',
      params: { accountId, conversationId: id },
    });
  } else {
    router.replace({ name: 'data_assistant_index', params: { accountId } });
  }
};

const selectConversation = async id => {
  selectedConversationId.value = id;
  navigateToConversation(id);
  await store.dispatch('dataAssistant/fetchConversation', id);
  scrollToBottom();
};

onMounted(async () => {
  await store.dispatch('dataAssistant/fetchConversations');
  const paramId = Number(route.params.conversationId);
  if (paramId) {
    await selectConversation(paramId);
  } else if (conversations.value.length) {
    await selectConversation(conversations.value[0].id);
  }
});

const createConversation = async () => {
  const conv = await store.dispatch('dataAssistant/createConversation');
  await selectConversation(conv.id);
};

// Message content editing
let msgEditReady = false;
const startEditingMsg = async msg => {
  msgEditReady = false;
  editingMsgId.value = msg.id;
  editingMsgContent.value = msg.content;
  await nextTick();
  msgEditReady = true;
};

const saveMsgContent = async () => {
  if (!msgEditReady) return;
  const content = editingMsgContent.value.trim();
  const messageId = editingMsgId.value;
  editingMsgId.value = null;
  if (!content || !messageId) return;
  await store.dispatch('dataAssistant/updateMessage', {
    conversationId: selectedConversationId.value,
    messageId,
    content,
  });
};

const cancelEditingMsg = () => {
  editingMsgId.value = null;
};

const confirmDeleteConversation = id => {
  pendingDeleteId.value = id;
  deleteDialogRef.value?.open();
};

const deleteConversation = async () => {
  const id = pendingDeleteId.value;
  deleteDialogRef.value?.close();
  await store.dispatch('dataAssistant/deleteConversation', id);
  if (selectedConversationId.value === id) {
    selectedConversationId.value = null;
    navigateToConversation(null);
  }
};

const askQuestion = async () => {
  const q = question.value.trim();
  if (!q || !selectedConversationId.value) return;

  question.value = '';

  try {
    await store.dispatch('dataAssistant/ask', {
      conversationId: selectedConversationId.value,
      question: q,
    });
  } catch {
    // error already handled in store
  }
};

// Refresh conversation list when assistant finishes (title may have changed)
watch(
  () => uiFlags.value.isAsking,
  (newVal, oldVal) => {
    if (oldVal && !newVal) {
      store.dispatch('dataAssistant/fetchConversations');
    }
  }
);

const handleKeyDown = event => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    askQuestion();
  }
};

const toggleSaveDropdown = async msgId => {
  if (saveDropdownMsgId.value === msgId) {
    saveDropdownMsgId.value = null;
    showNewReportInput.value = false;
    return;
  }
  saveDropdownMsgId.value = msgId;
  showNewReportInput.value = false;
  newReportName.value = '';
  await store.dispatch('dataAssistantReports/fetchReports');
};

const closeSaveDropdown = () => {
  saveDropdownMsgId.value = null;
  showNewReportInput.value = false;
};

const saveToReport = async (msg, reportId) => {
  await store.dispatch('dataAssistantReports/addItem', {
    reportId,
    message_id: msg.id,
  });
  closeSaveDropdown();
  useAlert(t('DATA_ASSISTANT.REPORT_SAVED'));
};

const createReportAndSave = async msg => {
  const name = newReportName.value.trim();
  if (!name) return;
  const report = await store.dispatch('dataAssistantReports/createReport', {
    name,
  });
  await saveToReport(msg, report.id);
};

const openConversationFilterHandler = (msg, payload) => {
  openConversationFilter(router, 'dam', msg, payload);
};

const exportCsv = async msg => {
  const { data } = await DataAssistantAPI.downloadMessageCsv(
    selectedConversationId.value,
    msg.id
  );
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'export.csv';
  a.click();
  URL.revokeObjectURL(url);
};
</script>

<template>
  <div class="flex h-full w-full overflow-hidden bg-n-background">
    <!-- Sidebar: conversation list -->
    <div
      class="flex w-72 shrink-0 flex-col border-r border-n-weak bg-n-solid-2"
    >
      <div
        class="flex items-center justify-between border-b border-n-weak px-4 py-3"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('DATA_ASSISTANT.TITLE') }}
        </h2>
        <button
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          :disabled="uiFlags.isCreating"
          @click="createConversation"
        >
          {{ t('DATA_ASSISTANT.NEW_CONVERSATION') }}
        </button>
      </div>
      <div class="flex-1 overflow-y-auto">
        <div
          v-if="uiFlags.isFetchingConversations"
          class="p-4 text-sm text-n-slate-11"
        >
          {{ t('DATA_ASSISTANT.LOADING') }}
        </div>
        <template v-else>
          <div
            v-for="conv in conversations"
            :key="conv.id"
            class="flex cursor-pointer items-center justify-between border-b border-n-weak px-4 py-3 hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-2': selectedConversationId === conv.id,
            }"
            @click="selectConversation(conv.id)"
          >
            <span class="truncate text-sm text-n-slate-12">
              {{ conv.title || t('DATA_ASSISTANT.UNTITLED') }}
            </span>
            <button
              class="ml-2 shrink-0 text-xs text-n-slate-10 hover:text-n-ruby-11"
              @click.stop="confirmDeleteConversation(conv.id)"
            >
              {{ t('DATA_ASSISTANT.DELETE') }}
            </button>
          </div>
          <div v-if="!conversations.length" class="p-4 text-sm text-n-slate-11">
            {{ t('DATA_ASSISTANT.NO_CONVERSATIONS') }}
          </div>
        </template>
      </div>
    </div>

    <!-- Main: chat area -->
    <div class="flex min-w-0 flex-1 flex-col">
      <template v-if="selectedConversationId">
        <!-- Messages -->
        <div
          ref="messagesContainer"
          class="flex-1 space-y-4 overflow-y-auto p-6"
        >
          <div
            v-if="uiFlags.isFetchingMessages"
            class="text-sm text-n-slate-11"
          >
            {{ t('DATA_ASSISTANT.LOADING_MESSAGES') }}
          </div>
          <template v-else>
            <div
              v-for="(msg, idx) in messages"
              :key="msg.id || idx"
              class="max-w-3xl"
              :class="msg.role === 'user' ? 'ml-auto' : ''"
            >
              <!-- User message -->
              <div
                v-if="msg.role === 'user'"
                class="rounded-lg bg-n-brand px-4 py-2 text-sm text-white"
              >
                {{ msg.content }}
              </div>

              <!-- Assistant message -->
              <div
                v-else
                class="space-y-2 rounded-lg bg-n-solid-3 px-4 py-3 text-sm"
              >
                <div class="flex items-start justify-between gap-2">
                  <textarea
                    v-if="editingMsgId === msg.id"
                    v-model="editingMsgContent"
                    class="min-w-0 flex-1 rounded border border-n-brand bg-n-solid-1 px-2 py-1 text-sm text-n-slate-12 outline-none"
                    rows="2"
                    @keydown.enter.exact="saveMsgContent"
                    @keydown.escape="cancelEditingMsg"
                    @blur="saveMsgContent"
                    @vue:mounted="
                      ({ el }) => {
                        el.focus();
                        el.style.height = el.scrollHeight + 'px';
                      }
                    "
                  />
                  <div
                    v-else
                    class="group/title min-w-0 flex-1 cursor-pointer"
                    @dblclick="startEditingMsg(msg)"
                  >
                    <span class="text-n-slate-12">
                      {{ msg.content }}
                    </span>
                    <button
                      class="ml-1 inline-flex align-middle rounded p-0.5 text-n-slate-10 opacity-0 hover:text-n-slate-12 group-hover/title:opacity-100"
                      :title="t('DATA_ASSISTANT.EDIT_MESSAGE')"
                      @click="startEditingMsg(msg)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-3 w-3"
                      >
                        <path
                          d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z"
                        />
                      </svg>
                    </button>
                  </div>
                  <div
                    v-if="msg.result_data && msg.result_type !== 'error'"
                    class="flex shrink-0 gap-1"
                  >
                    <!-- Export CSV button -->
                    <button
                      class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                      :title="t('DATA_ASSISTANT.EXPORT_CSV')"
                      @click="exportCsv(msg)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-4 w-4"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 3a.75.75 0 01.75.75v7.69l2.72-2.72a.75.75 0 111.06 1.06l-4 4a.75.75 0 01-1.06 0l-4-4a.75.75 0 111.06-1.06l2.72 2.72V3.75A.75.75 0 0110 3zM3.75 15a.75.75 0 000 1.5h12.5a.75.75 0 000-1.5H3.75z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                    <!-- Save to report button -->
                    <div class="relative">
                      <button
                        class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                        :title="t('DATA_ASSISTANT.SAVE_TO_REPORT')"
                        @click="toggleSaveDropdown(msg.id)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="h-4 w-4"
                        >
                          <path
                            d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                          />
                        </svg>
                      </button>
                      <!-- Dropdown -->
                      <div
                        v-if="saveDropdownMsgId === msg.id"
                        class="absolute right-0 top-8 z-10 w-56 rounded-lg border border-n-weak bg-n-solid-1 py-1 shadow-lg"
                      >
                        <div
                          class="border-b border-n-weak px-3 py-1.5 text-xs font-semibold text-n-slate-11"
                        >
                          {{ t('DATA_ASSISTANT.SAVE_TO_REPORT') }}
                        </div>
                        <button
                          v-for="report in reports"
                          :key="report.id"
                          class="block w-full truncate px-3 py-1.5 text-left text-xs text-n-slate-12 hover:bg-n-alpha-2"
                          @click="saveToReport(msg, report.id)"
                        >
                          {{ report.name }}
                        </button>
                        <div
                          v-if="!showNewReportInput"
                          class="border-t border-n-weak"
                        >
                          <button
                            class="block w-full px-3 py-1.5 text-left text-xs font-medium text-n-brand hover:bg-n-alpha-2"
                            @click="showNewReportInput = true"
                          >
                            {{ t('DATA_ASSISTANT.NEW_REPORT') }}
                          </button>
                        </div>
                        <div v-else class="border-t border-n-weak px-3 py-2">
                          <input
                            v-model="newReportName"
                            class="mb-1.5 w-full rounded border border-n-weak bg-n-solid-2 px-2 py-1 text-xs text-n-slate-12 outline-none focus:border-n-brand"
                            :placeholder="t('DATA_ASSISTANT.REPORT_NAME')"
                            @keydown.enter="createReportAndSave(msg)"
                          />
                          <button
                            class="w-full rounded bg-n-brand px-2 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                            :disabled="!newReportName.trim()"
                            @click="createReportAndSave(msg)"
                          >
                            {{ t('DATA_ASSISTANT.NEW_REPORT') }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Error -->
                <div
                  v-if="
                    msg.result_type === 'error' &&
                    msg.error_message &&
                    msg.error_message !== msg.content
                  "
                  class="rounded bg-n-ruby-3 p-2 font-mono text-xs text-n-ruby-11"
                >
                  {{ msg.error_message }}
                </div>

                <!-- Generated SQL -->
                <details v-if="msg.generated_sql" class="text-xs">
                  <summary
                    class="cursor-pointer text-n-slate-10 hover:text-n-slate-12"
                  >
                    {{ t('DATA_ASSISTANT.SHOW_SQL') }}
                  </summary>
                  <pre
                    class="mt-1 overflow-x-auto rounded bg-n-solid-1 p-2 text-n-slate-11"
                    >{{ msg.generated_sql }}
                  </pre>
                </details>

                <!-- Result data -->
                <ResultVisualization
                  v-if="msg.result_data && Object.keys(msg.result_data).length"
                  :result-type="msg.result_type"
                  :result-data="msg.result_data"
                  :conv-filter-params="msg.conv_filter_params"
                  @segment-click="
                    payload => openConversationFilterHandler(msg, payload)
                  "
                />
              </div>
            </div>
          </template>

          <!-- Typing / progress indicator -->
          <div
            v-if="uiFlags.isAsking"
            class="max-w-3xl rounded-lg bg-n-solid-3 px-4 py-3 text-sm text-n-slate-11"
          >
            {{ progressMessage || t('DATA_ASSISTANT.THINKING') }}
          </div>
        </div>

        <!-- Input -->
        <div class="shrink-0 px-4 py-3">
          <div
            class="relative mx-auto max-w-3xl rounded-2xl border border-n-weak bg-n-solid-1 px-4 pb-3 pt-3 shadow-sm focus-within:border-n-slate-8"
          >
            <textarea
              ref="questionTextarea"
              v-model="question"
              class="!m-0 block w-full resize-none border-0 bg-transparent !p-0 text-sm text-n-slate-12 !outline-none !shadow-none placeholder:text-n-slate-10 focus:!outline-none focus:!shadow-none focus:!ring-0"
              rows="1"
              :placeholder="t('DATA_ASSISTANT.PLACEHOLDER')"
              :disabled="uiFlags.isAsking"
              @keydown="handleKeyDown"
            />
            <div class="mt-2 flex items-center justify-end">
              <button
                class="flex h-8 w-8 items-center justify-center rounded-full bg-n-slate-12 text-n-solid-1 transition-opacity hover:opacity-80 disabled:opacity-30"
                :disabled="uiFlags.isAsking || !question.trim()"
                @click="askQuestion"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="h-4 w-4"
                >
                  <path
                    d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"
                    transform="rotate(-90 12 12)"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state -->
      <div
        v-else
        class="flex flex-1 items-center justify-center text-n-slate-11"
      >
        <p>{{ t('DATA_ASSISTANT.EMPTY_STATE') }}</p>
      </div>
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('DATA_ASSISTANT.DELETE_CONFIRM')"
      :description="t('DATA_ASSISTANT.DELETE_CONFIRM_DESCRIPTION')"
      :confirm-button-label="t('DATA_ASSISTANT.DELETE_CONFIRM_YES')"
      @confirm="deleteConversation"
    />
  </div>
</template>
